/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
import { ApplyCouponSuggestion } from "./Coupon";
import { CouponlessSavingsWidgetInfo } from "./CouponlessSavings";
import { CartItem } from "./menu-items";
import { ApiMenuCartItem, ICartSavedPreferences, NativeLocalCart } from "./LocalCart";
import { CouponDetails, ReviewedCartApiResponse } from "./ReviewedCart";

export type IMStoreId = string | number;

export enum IMServiceabilityStatus {
    SERVICEABLE = 2,
    TEMP_UNSERVICEABLE = 1,
    UNSERVICEABLE = 0,
}
export interface IMShipmentDetailsV2Meta {
    title: string;
    description?: string;
    rxShipment?: boolean;
    footerInfo?: {
        text: string;
        icon: string;
    };
}

export interface IMStoreCategory {
    name: string;
    id: number;
}

export const enum IMCategoryCancellationPolicy {
    Liquor = "Liquor",
}

export interface IMCategoryTypeMaxAllowedWeights {
    [key: string]: number;
}

export interface IMStore {
    name: string;
    address: string;
    imageId: string;
    id: number;
    isPartner: boolean;
    location: Location;
    categories: IMStoreCategory[];
    landmark: string;
    locality: string;
    maxAllowedCartWeightInGrams: number;
    maxAllowedCartVolumeInCc: number;
    maxAllowedItemsInCart: number;
    categoryCancellationPolicy: IMCategoryCancellationPolicy | null;
    minimumOrderValueInRs?: number;
    subCategoryTypeMaxAllowedWeights?: IMCategoryTypeMaxAllowedWeights;
}

export enum StructureItemType {
    SLASHED_DEAL = "SLASHED_DEAL",
    PRINT = "PRINT",
    FLASH_SALE = "FLASH_SALE",
    FREEBIE = "FREEBIE",
}

export enum ServiceLine {
    INSTAMART = "INSTAMART",
}

export interface IInstamartGiftingWidgetCta {
    text: string;
    giftBagPackagingEnabled: boolean;
}

export interface IInstamartGiftingWidgetPopUpDetails {
    description: string;
    backgroundImageCreativeId: string;
    creativeId: string;
    ctaButtons: [IInstamartGiftingWidgetCta, IInstamartGiftingWidgetCta];
    disclaimer: string;
}

export interface IMGiftBagItem {
    variations: IMItemVariation[];
    available: boolean;
    inStockAndSlotAvailable: boolean;
    avail: boolean;
    product_id: string;
    display_name: string;
    is_avail: boolean;
    in_stock: boolean;
    brand_id: string;
    product_name_without_brand: string;
}

export interface IGiftBag {
    widgetInfo: {
        title: string;
        description: string;
        creativeId: string;
        popupDetails: IInstamartGiftingWidgetPopUpDetails;
    };
    data: IMGiftBagItem[];
}

export enum IGiftingInternalBusinessLine {
    FOOD = "FOOD",
    INSTAMART = "INSTAMART",
    GIFTING = "GIFTING",
}

export enum GiftingCartItemType {
    FOOD = "FOOD",
    INSTAMART = "INSTAMART",
    GIFTING = "GIFTING",
    INSTAMART_GIFT_BAG = "INSTAMART_GIFT_BAG",
}

export enum IMStructureItemType {
    SLASHED_DEAL = "SLASHED_DEAL",
    PRINT = "PRINT",
    FLASH_SALE = "FLASH_SALE",
    FREEBIE = "FREEBIE",
}

export interface IInventory {
    in_stock: boolean;
}

export interface ICartAllowedQty {
    total: number;
    message: string;
}

export interface Dimensions {
    length_in_cm: number;
    width_in_cm: number;
    height_in_cm: number;
    volume_in_cc: number;
}

export interface IMVariationTag {
    type: string;
    icon: { creative_id: string };
    description: string;
    valid_prescription?: boolean;
    downloadable_prescription_url?: string;
}

export interface IMediaAspectRatio {
    width: number;
    height: number;
}

export interface IMVideoType {
    src: string;
    thumbnail: string;
    aspect_ratio: IMediaAspectRatio;
}

export interface IMItemVariation {
    default: boolean;
    id: string;
    images?: string[];
    videos?: IMVideoType[];
    display_name: string;
    brand?: string;
    product_name_without_brand?: string;
    coupon_less_offers?: string[];
    meta?: {
        disclaimer?: string;
        short_description?: string;
        long_description?: string;
        groups?: string[];
        coupon_less_offers?: string[];
    };
    price: {
        mrp: number;
        store_price: number;
        offer_price: number;
        discount_value?: number;
        offer_applied?: {
            listing_description: string;
            offer_id: string;
            product_description: string;
            super_offer?: boolean;
        };
        unit_level_price?: string;
        max_saver_price?: {
            offer_price: number;
            unit_level_price?: string;
            offer_details: {
                listing_description: string;
                mov_threshold: number;
            };
        };
        flash_sale_price_details?: {
            flash_sale_price: number;
            non_flash_sale_price: number;
            non_flash_sale_listing_description: string;
            redemption_limit_exceeded_message: string;
            non_flash_sale_unit_level_price: string;
            redemption_units: number;
            offer_details?: {
                offer_id: string;
                listing_description: string;
                product_description: string;
                super_offer: boolean;
                mov_threshold: number;
            };
        };
    };
    inventory?: IInventory;
    tags?: string[];
    quantity: string;
    unit_of_measure?: string;
    spin: string;
    max_allowed_quantity: number;
    cart_allowed_quantity: ICartAllowedQty;
    volumetric_weight: number;
    weight_in_grams: number;
    alcohol_content_percentage?: number;
    container_type?: string;
    sub_category_type?: string;
    sub_category?: string;
    super_category?: string;
    category?: string;
    dimensions?: Dimensions;
    sku_slot_info?: {
        is_avail?: boolean;
        message?: string;
    };
    groups?: Array<{ [key: string]: string }>;
    super_saver?: boolean;
    listing_variant?: boolean;
    sku_quantity_with_combo?: string;
    sku_secondary_quantity?: string;
    last_ordered_at?: number;
    store_id: string;
    rx_required?: boolean;
    external_pharmacy_item?: boolean;
    variation_tags?: IMVariationTag[];
    print_attributes?: {
        doc_id: string;
        item_id: string;
        category: string;
        no_of_pages_per_copy: number;
        no_of_copies: number;
        subtext: string;
        print_icon: string;
    };
}

export enum IMItemCardView {
    DEFAULT = "DEFAULT",
    V2 = "V2",
}

export const enum ITEM_BADGE_TYPE {
    AD = "AD",
    SOURCE = "SOURCE",
    PREV_BOUGHT = "PREVIOUSLY_BOUGHT",
}

export interface IMBadgeInfo {
    badge_type: ITEM_BADGE_TYPE;
    badge_text: string;
    badge_color?: string;
    text_color?: string;
}

export interface IMBaseItemGroup {
    display_name: string;
    brand?: string;
    product_name_without_brand?: string;
    in_stock: boolean;
    is_avail: boolean;
    item_card_view?: IMItemCardView;
    variations: IMItemVariation[];
    product_id: string;
    ad_tracking_context?: string;
    name_slug?: string;
    inline_message?: {
        text: string;
    };
    badge_info?: IMBadgeInfo;
    badges?: IMBadgeInfo[];
}

export interface IMItemGroup extends IMBaseItemGroup {
    avail: boolean;
    max_allowed_quantity: number;
    weight_in_grams: number;
    sub_category_type: string;
    gift_bag_item?: boolean;
}

export interface IMBaseItem {
    quantity: number;
    storeId?: number | string;
    shipmentId?: string;
    shipmentIdV2?: string;
}

export interface IMUnStructureItem extends IMBaseItem {
    itemId: string;
    name?: string;
    imageIds?: string[];
    comment?: string;
    variant?: {
        description: string;
    };
    type: string;
}

export interface IMStructureItem extends IMBaseItem {
    type: string;
    quantity: number;
    itemId: string;
    price: number;
    productId: string;
    freebie?: boolean;
    tradeFreebie?: boolean;
    imageIds?: string[];
    name?: string;
    spin: string;
    storeId: number | string;
    metadata?: IMItemGroup;
    isGiftBag?: boolean;
    shipmentId?: string;
    shipmentIdV2?: string;
    itemType?: IMStructureItemType;
    outOfStock?: boolean;
    stockLimited?: boolean;
}

export type IMItem = IMUnStructureItem | IMStructureItem;

export interface IMFilteredStructureItem extends IMBaseItem {
    itemId: string;
    name?: string;
    serviceLine?: "INSTAMART";
    productId?: string;
    storeId?: number | string;
    meta?: {
        type: string;
        freebie?: boolean;
        isGiftBag?: boolean;
        itemType?: IMStructureItemType;
    };
    spin: string;
}

export interface GiftingCouponCode {
    code: string;
    businessLine: string;
    savings?: string;
}

export interface IMServiceabilityDetails {
    serviceabilityStatus: IMServiceabilityStatus;
    sla: string;
    slaTime: number;
}

export interface IMShipmentDetails {
    id: string;
    storeId: string;
    icon: string;
    serviceabilityDetails: IMServiceabilityDetails;
    meta: IMShipmentDetailsV2Meta;
    disclaimer?: string;
}

export interface GiftingCouponDetails extends Omit<CouponDetails, "coupon_codes"> {
    coupon_codes?: GiftingCouponCode[];
}

export interface GiftingApplyCouponWidgetInfo extends ApplyCouponSuggestion {
    coupon_codes?: GiftingCouponCode[];
}

export interface GiftingLocalCart extends NativeLocalCart {
    address_id?: string;
    foodCartItems?: ApiMenuCartItem[];
    imCartItems?: IMFilteredStructureItem[];
    cart_journey_ctx?: {
        cart_initialization_source?: string;
        cart_category_type?: string;
        request_contexts?: string[];
        additional_context?: { [key: string]: string };
    };
    couponCodes?: GiftingCouponCode[];
    restaurantId?: number | string;
    restaurantName?: string;
    tipDetails?: {
        amount: number;
        optIn: boolean;
    };
    subscriptionItems?: {
        plan_id: string;
        quantity: number;
    }[];
    cartLatLong?: {
        lat: string | number;
        lng: string | number;
    };
    userLiveLat?: string | number;
    userLiveLng?: string | number;
    cart_type?: string;
    orderComment?: string;
    cutleryComments?: string;
    sld?: boolean;
    date?: string;
    cart_saved_preferences?: ICartSavedPreferences;
}

export interface GiftingCartApiResponse extends ReviewedCartApiResponse {
    imGiftBag?: IGiftBag;
    imCartItems?: IMStructureItem[];
    foodCartItems?: {
        slaTime: number;
        isServiceable: boolean;
        items: CartItem[];
        disclaimer?: string;
    };
    couponCodes?: GiftingCouponCode[];
    imShipmentDetails?: IMShipmentDetails[];
    useGiftBagPackaging?: boolean;
    imCartAlertCrouton?: {
        icon: string;
        message: string;
    };
    imStoresInfo?: IMStore[];
    im_couponless_savings_widget_info?: CouponlessSavingsWidgetInfo;
    apply_coupon_widget_info?: GiftingApplyCouponWidgetInfo;
    savings_message?: string;
}

export interface CombinedCartInStockItemRender {
    subCartId: string;
    isServiceable: boolean;
    slaTime: number;
    slaTimeString?: string;
    items?: CartItem[] | IMStructureItem[];
    businessLine: IGiftingInternalBusinessLine;
    slaTagImage?: string;
    giftBagItems?: IMGiftBagItem[];
    disclaimer?: string;
}

export interface FoodCartItemWithUUID extends CartItem {
    unId: string;
    itemPosition: number;
}

export interface IMCartItemWithUUID extends IMStructureItem {
    unId: string;
    itemPosition: number;
}

export interface IMGiftBagItemWithUUID extends IMGiftBagItem {
    unId: string;
    itemPosition: number;
}

export enum GiftBagSelectionMode {
    IN_THE_BAG = "IN_THE_BAG",
    OUT_OF_BAG = "OUT_OF_BAG",
}

export interface ItemLimitExceededItem {
    title: string;
    quantity: number;
    imageId: string;
    menu_item_id?: number;
    itemId?: string;
}
